import React from 'react';
import { Alert, Icon, Intent } from '@blueprintjs/core';
import { observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import Toast from 'helpers/toaster';
import { formatDateOnly } from 'helpers/time';
import * as DataList from 'components/DataList';

// Layout shared by the list and its loading skeleton.
export const CARD_COLUMNS = [
  { area: 'card', label: 'Card', width: 'minmax(220px, 1.6fr)', bone: 'single' },
  { area: 'expiry', label: 'Expires', width: 'minmax(90px, 0.6fr)', bone: 'short', icon: 'calendar' },
  { area: 'added_by', label: 'Added by', width: 'minmax(140px, 1fr)', bone: 'single', icon: 'person' },
  { area: 'added_on', label: 'Added on', width: 'minmax(120px, 0.8fr)', bone: 'short', icon: 'time' },
  { area: 'actions', label: '', width: '56px', bone: 'none' },
];

export const CARD_CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: ['card actions', 'expiry expiry', 'added_by added_by', 'added_on added_on'],
};

export const CARD_MIN_TABLE_WIDTH = 600;

const cardName = (card) => {
  const { stripe_card_brand: brand, last4 } = card.attributes;
  return `${brand || 'Card'}${last4 ? ` ending ${last4}` : ''}`;
};

// Billing > Card: the cards on file. Delete asks first.
@inject('billingStore') @observer
class CardList extends React.Component {
  @observable deleting = null; // the card waiting for delete confirmation

  componentDidMount() {
    this.props.billingStore.loadCards();
  }

  confirmDelete = () => {
    const card = this.deleting;
    this.deleting = null;
    this.props.billingStore.deleteCard(card)
      .then(() => {
        Toast.show({ message: `${cardName(card)} deleted.`, intent: Intent.SUCCESS });
      })
      .catch(() => {
        Toast.show({ message: `Couldn't delete the ${cardName(card)}. Try again.`, intent: Intent.DANGER });
      });
  };

  renderList(objects) {
    if (objects.length === 0) {
      return (
        <DataList.Notice
          icon="credit-card"
          title="No card on file"
          message="Add the card your subscription is charged to below."
        />
      );
    }
    return (
      <DataList.List columns={CARD_COLUMNS} cards={CARD_CARDS} minTableWidth={CARD_MIN_TABLE_WIDTH} total={objects.length} noun="cards">
        {objects.map((card) => {
          const a = card.attributes;
          return (
            <DataList.Row key={a.id}>
              <DataList.Cell area="card">
                <div className="sp-name">
                  <span className="bill-card-mark" aria-hidden="true"><Icon icon="credit-card" iconSize={14} /></span>
                  <DataList.Lines primary={`${a.stripe_card_brand || 'Card'} •••• ${a.last4}`} />
                  {a.is_default && <DataList.Pill tone="blue">Primary</DataList.Pill>}
                </div>
              </DataList.Cell>
              <DataList.Cell area="expiry">{a.card_expiry}</DataList.Cell>
              <DataList.Cell area="added_by">{a.added_by || <DataList.Muted>–</DataList.Muted>}</DataList.Cell>
              <DataList.Cell area="added_on">{a.created_at ? formatDateOnly(a.created_at) : <DataList.Muted>–</DataList.Muted>}</DataList.Cell>
              <DataList.Cell area="actions">
                <DataList.Actions
                  items={[{ icon: 'trash', label: `Delete ${cardName(card)}`, onClick: () => { this.deleting = card; }, intent: Intent.DANGER }]}
                />
              </DataList.Cell>
            </DataList.Row>
          );
        })}
      </DataList.List>
    );
  }

  render() {
    const { billingStore } = this.props;
    const { isLoading, isError, objects } = billingStore.cards;
    let content;
    if (isLoading && objects.length === 0) {
      content = <DataList.Loading columns={CARD_COLUMNS} cards={CARD_CARDS} minTableWidth={CARD_MIN_TABLE_WIDTH} noun="cards" rows={2} />;
    } else if (isError) {
      content = <DataList.LoadError noFilters noun="cards" onRetry={() => billingStore.loadCards()} />;
    } else {
      content = this.renderList(objects);
    }
    return (
      <React.Fragment>
        {content}
        <Alert
          isOpen={Boolean(this.deleting)}
          icon="trash"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Delete card"
          onCancel={() => { this.deleting = null; }}
          onConfirm={this.confirmDelete}
        >
          <strong>{this.deleting ? `Delete the ${cardName(this.deleting)}?` : 'Delete this card?'}</strong>
          <p>It's removed from your account and can't be used for future payments.</p>
        </Alert>
      </React.Fragment>
    );
  }
}

export default CardList;
